import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { replaceSection, updateCartCount } from '@theme/section-renderer';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.status = this.querySelector('[data-product-form-status]');
    this.controller = new AbortController();
    this.form?.addEventListener('submit', this.onSubmit, { signal: this.controller.signal });
    this.form?.querySelector('[data-product-quantity]')?.addEventListener('input', this.updateVolumePrice, { signal: this.controller.signal });
  }

  updateVolumePrice = (event) => {
    const output = this.form?.querySelector('[data-volume-current-price]');
    const data = this.form?.querySelector('[data-quantity-price-breaks]');
    if (!(output instanceof HTMLElement) || !(data instanceof HTMLScriptElement)) return;
    try {
      const breaks = JSON.parse(data.textContent || '[]');
      const quantity = Number(event.currentTarget.value);
      const match = [...breaks].reverse().find((item) => quantity >= Number(item.quantity));
      output.textContent = match?.price || output.dataset.basePrice || output.textContent;
    } catch {}
  };

  disconnectedCallback() {
    this.controller?.abort();
  }

  onSubmit = async (event) => {
    if (!window.fetch || !document.getElementById('CartDrawer')) return;
    event.preventDefault();

    const submitButton = this.form.querySelector('[type="submit"][name="add"]');
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity')) || 1;
    const variantId = String(formData.get('id') || '');
    const deferred = CartLinesUpdateEvent.createPromise();
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    }));

    submitButton?.setAttribute('aria-disabled', 'true');
    if (submitButton) submitButton.disabled = true;
    if (this.status) this.status.textContent = '';

    try {
      const response = await fetch(document.documentElement.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message);

      let itemCount = quantity;
      let cart = { items: result.items || [], item_count: itemCount };
      if (result.sections?.['cart-drawer']) {
        const drawer = replaceSection('cart-drawer', result.sections['cart-drawer'], { mode: 'hydration' });
        itemCount = Number(drawer?.querySelector('[data-cart-item-count]')?.dataset.cartItemCount) || itemCount;
        cart = { ...cart, item_count: itemCount };
      }
      updateCartCount(itemCount);
      if (this.status) this.status.textContent = this.dataset.successMessage || '';
      deferred.resolve({
        cart: null,
        detail: {
          didError: false,
          items: cart.items || [],
          source: 'product-form',
          sourceId: this.form.id,
          itemCount,
          productId: this.dataset.productId || '',
        },
      });

      const drawer = document.getElementById('CartDrawer')?.closest('modal-dialog');
      if (drawer && typeof drawer.open === 'function') drawer.open(submitButton);
    } catch (error) {
      const message = error.message || this.dataset.errorMessage;
      if (this.status) this.status.textContent = message;
      this.dispatchEvent(new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: message, errors: message },
      }));
      deferred.reject(error);
    } finally {
      submitButton?.removeAttribute('aria-disabled');
      if (submitButton) submitButton.disabled = false;
    }
  };
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
